import React, { useContext, useEffect, useRef, useState } from 'react';
import { ZdsAdvancedSelect, ZdsButton, ZdsDivider, ZdsIcon, ZdsAdvancedSelectItem, ZdsSpinner, } from '@zig-design-system/ui-components-react';
import AffordabilityScale from './AffordabilityScale';
import { PriceTable, PriceTableDividerRow, PriceTableRow } from '../../../../Shared/components/PriceTable';
import Translation from '../../../../Shared/components/Translation';
import { renderToString } from 'react-dom/server';
import HousingCostsModal from './HousingCostsModal';
import { RootContext } from '../../../../Context/RootContext';
const HousingCosts = ({ unit }) => {
    const selfRef = useRef(null);
    const modalRef = useRef(null);
    const storedRememberData = JSON.parse(localStorage.getItem('housingCostsRememberData'));
    const storedResidents = JSON.parse(localStorage.getItem('housingCostsResidents'));
    const storedChosenProfile = JSON.parse(localStorage.getItem('housingCostsChosenProfile'));
    const [rememberData, setRememberData] = useState(storedRememberData !== null ? storedRememberData : true);
    const [residents, setResidents] = useState(storedResidents || []);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [showPersonal, setShowPersonal] = useState(residents.length > 0);
    const [chosenProfile, setChosenProfile] = useState(storedChosenProfile !== null ? storedChosenProfile : 0);
    const [woonlastenProfielen, setWoonlastenProfielen] = useState(null);
    const pageSettings = useContext(RootContext);
    const shownProfile = showPersonal ? woonlastenProfielen === null || woonlastenProfielen === void 0 ? void 0 : woonlastenProfielen[0] : woonlastenProfielen === null || woonlastenProfielen === void 0 ? void 0 : woonlastenProfielen[chosenProfile];
    const openModal = () => {
        var _a;
        (_a = modalRef.current) === null || _a === void 0 ? void 0 : _a.open();
    };
    const scrollToTopOfComponent = () => {
        var _a;
        (_a = selfRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth' });
    };
    useEffect(() => {
        if (woonlastenProfielen !== null) {
            return;
        }
        const type = showPersonal ? 'persoonlijkewoonlasten' : 'woonlasten';
        const params = new URLSearchParams({
            tenant: pageSettings.pluginContext.woonlastenServiceTenant,
            rekenhuur: unit.eligibleRent,
            servicekosten: unit.serviceCosts,
            postcode: unit.postalCode,
            huisnummer: unit.houseNumber,
        });
        if (unit.houseNumberAddition) {
            params.append('huisnummertoevoeging', unit.houseNumberAddition);
        }
        if (showPersonal) {
            residents.forEach((resident, index) => {
                params.append(`personen[${index}][inkomen]`, resident.income.toString());
                params.append(`personen[${index}][geboortedatum]`, resident.birthdate);
            });
        }
        let url = `https://energielasten.hexia.io/api/v1/vabi/${type}?${params.toString()}`;
        fetch(url)
            .then((response) => {
            if (!response.ok) {
                return Promise.reject();
            }
            return response.json();
        })
            .then((data) => {
            if (data.message) {
                return Promise.reject();
            }
            setWoonlastenProfielen(data.profielen);
        })
            .catch(() => {
            setError(true);
        })
            .finally(() => {
            setLoaded(true);
        });
    }, [woonlastenProfielen]);
    const handleModalSaved = (formData) => {
        setLoaded(false);
        setResidents(formData.residents);
        setRememberData(formData.rememberData);
        setWoonlastenProfielen(null);
        setShowPersonal(true);
        localStorage.setItem('housingCostsRememberData', JSON.stringify(formData.rememberData));
        if (formData.rememberData) {
            localStorage.setItem('housingCostsResidents', JSON.stringify(formData.residents));
        }
        else {
            localStorage.removeItem('housingCostsResidents');
        }
        scrollToTopOfComponent();
    };
    const handleRemovePersonalDataButtonClicked = () => {
        setLoaded(false);
        setWoonlastenProfielen(null);
        setResidents([]);
        setShowPersonal(false);
        localStorage.removeItem('housingCostsResidents');
        scrollToTopOfComponent();
    };
    const profileOptions = [
        {
            id: 0,
            label: React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.profile0Label" }),
            secondaryText: React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.profile0SecondaryText" }),
            iconName: 'person-simple',
            iconLibrary: 'fa-solid',
        },
        {
            id: 1,
            label: React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.profile1Label" }),
            secondaryText: React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.profile1SecondaryText" }),
            iconName: 'people-pants-simple',
            iconLibrary: 'fa-solid',
        },
        {
            id: 2,
            label: React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.profile2Label" }),
            secondaryText: React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.profile2SecondaryText" }),
            iconName: 'family',
            iconLibrary: 'fa-solid',
        },
        {
            id: 3,
            label: React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.profile3Label" }),
            secondaryText: React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.profile3SecondaryText" }),
            iconName: 'family_four',
            iconLibrary: 'zig',
        },
    ];
    const handleChosenProfileChanged = (event) => {
        const items = event.detail;
        if (!items.length)
            return;
        const value = parseInt(items[0]['itemId']);
        setChosenProfile(value);
        localStorage.setItem('housingCostsChosenProfile', JSON.stringify(value));
    };
    if (!unit.postalCode || !unit.houseNumber || !unit.eligibleRent) {
        return null;
    }
    if (error) {
        return null;
    }
    return (React.createElement("div", { className: "housing-costs", ref: selfRef },
        React.createElement("h2", { className: "zds-headline-md" },
            React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.header" })),
        !loaded ? (React.createElement(ZdsSpinner, { indeterminate: true, className: "zds-margin-top-4 zds-margin-bottom-12" })) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "zds-body zds-margin-bottom-12" }, showPersonal ? (React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.introPersonal" })) : (React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.introNotPersonal" }))),
            React.createElement("div", { className: "zds-grid md:zds-grid-cols-2 zds-gap-12 zds-margin-top-8" },
                React.createElement("div", null,
                    !showPersonal && (React.createElement("div", { className: "zds-margin-bottom-6" },
                        React.createElement(ZdsAdvancedSelect, { onZdsChange: handleChosenProfileChanged, "text-type": "multiline", "full-width": true, label: renderToString(React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.selectHouseholdSize" })) }, profileOptions.map((option) => (React.createElement(ZdsAdvancedSelectItem, { "item-id": option.id, key: option.id, 
                            /* Zds has a bug where selected={false} is still seen as seleted */
                            selected: option.id === chosenProfile || undefined },
                            React.createElement(ZdsIcon, { slot: "content-start", library: option.iconLibrary, name: option.iconName, size: "md" }),
                            React.createElement("span", { slot: "primary-text" }, option.label),
                            React.createElement("span", { slot: "secondary-text" }, option.secondaryText))))))),
                    showPersonal && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "zds-headline-sm zds-margin-bottom-2" },
                            React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.personalHousingCosts" })),
                        shownProfile.betaalbaarheidscore > -1 && (React.createElement(AffordabilityScale, { affordabilityScore: shownProfile.betaalbaarheidscore + 1, className: "zds-margin-bottom-12" })))),
                    shownProfile && (React.createElement(PriceTable, null,
                        React.createElement(PriceTableRow, { label: renderToString(React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.rent", asString: true })), amount: unit.eligibleRent }),
                        React.createElement(PriceTableRow, { label: renderToString(React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.serviceCosts", asString: true })), amount: shownProfile.servicekosten }),
                        React.createElement(PriceTableRow, { label: renderToString(React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.energyCosts", asString: true })), amount: shownProfile.energielasten / 12 }),
                        showPersonal && (React.createElement(PriceTableRow, { label: renderToString(React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.huurtoeslag", asString: true })), amount: -(shownProfile.huurtoeslag / 12) })),
                        React.createElement(PriceTableDividerRow, null),
                        React.createElement(PriceTableRow, { className: "zds-bold", label: renderToString(React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.maandlasten", asString: true })), amount: (showPersonal
                                ? shownProfile.woonlasten
                                : shownProfile.woonlasten + shownProfile.huurtoeslag) / 12 }))),
                    showPersonal && (React.createElement("div", { className: "zds-grid zds-gap-4 zds-margin-top-8" },
                        React.createElement(ZdsButton, { onZdsClick: openModal },
                            React.createElement(ZdsIcon, { library: "fa-solid", name: "pencil", size: "sm" }),
                            React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.editPersonalDataButton" })),
                        React.createElement(ZdsButton, { preset: "neutral", onZdsClick: handleRemovePersonalDataButtonClicked },
                            React.createElement(ZdsIcon, { library: "fa-solid", name: "trash", size: "sm" }),
                            React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.removePersonalDataButton" }))))),
                React.createElement("div", null,
                    showPersonal && (React.createElement(React.Fragment, null,
                        shownProfile.betaalbaarheidscore > -1 && (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: "zds-headline-sm zds-margin-bottom-2" },
                                React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.explanationHeader" })),
                            React.createElement("div", { className: "zds-body" },
                                React.createElement(Translation, { item: `tx_zigrealestate.details.housingCosts.explanationTextForScore${shownProfile.betaalbaarheidscore + 1}` })))),
                        React.createElement("div", { className: "zds-bg-grey-50 zds-radius-md zds-padding-3 zds-margin-top-6" },
                            React.createElement("div", { className: "zds-headline-sm zds-margin-bottom-2" },
                                React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.disclaimerHeader" })),
                            React.createElement("div", { className: "zds-body" },
                                React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.disclaimerText", asHtml: true }))))),
                    !showPersonal && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "zds-headline-sm zds-margin-bottom-2" },
                            React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.personalHousingCosts" })),
                        React.createElement(ZdsButton, { onZdsClick: openModal, className: "zds-block" },
                            React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.personalCalculationButton" })),
                        React.createElement("div", { className: "zds-body zds-muted zds-margin-top-4" },
                            React.createElement(Translation, { item: "tx_zigrealestate.details.housingCosts.personalCalculationDescription" })))))))),
        React.createElement(ZdsDivider, { className: "zds-margin-y-4 md:zds-margin-y-8" }),
        React.createElement(HousingCostsModal, { savedData: { rememberData, residents }, onSave: handleModalSaved, ref: modalRef })));
};
export default HousingCosts;
